import React from "react";

import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ClipboardList,
  LineChart,
  ListChecks,
  Loader2,
  type LucideIcon,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import KpiCategoryPaneBody, {
  type KpiPaneAnchorKey,
  kpiPaneAnchorId,
} from "./KpiCategoryPaneBody";
import {
  buildMetricLookup,
  formatDMY,
  HeroScoreBubble,
  Tip,
} from "./KpiDetailBlocks";
import KpiMetricsDataCard from "./KpiMetricsDataCard";
import type {
  XFactorKpiCategoryDetailModel,
  XFactorKpiFullBriefing,
} from "../lib/deriveKpi";

/** xfactor's subsection jump bar: icon set, order and tooltips are its own. */
const ANCHORS: Array<{
  key: KpiPaneAnchorKey;
  label: string;
  Icon: LucideIcon;
  has: (c: XFactorKpiCategoryDetailModel) => boolean;
}> = [
  {
    key: "synopsis",
    label: "Synopsis",
    Icon: ClipboardList,
    has: c =>
      Boolean(c.synopsis || c.highlightsBrief || c.lowlightsBrief || c.roBrief),
  },
  {
    key: "highlights",
    label: "Highlights",
    Icon: TrendingUp,
    has: c => c.highlights.length > 0,
  },
  {
    key: "lowlights",
    label: "Lowlights",
    Icon: TrendingDown,
    has: c => c.lowlights.length > 0,
  },
  {
    key: "opportunities",
    label: "Opportunities",
    Icon: Target,
    has: c => c.opportunities.length > 0,
  },
  {
    key: "risks",
    label: "Risks",
    Icon: AlertTriangle,
    has: c => c.risks.length > 0,
  },
  {
    key: "actions",
    label: "Recommended Actions",
    Icon: ListChecks,
    has: c => c.actions.length > 0,
  },
  {
    key: "trends",
    label: "Key Trends",
    Icon: LineChart,
    has: c => c.trends.length > 0,
  },
];

const RailButton: React.FC<{
  Icon: LucideIcon;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ Icon, label, onClick, disabled }) => (
  <Tip side="right" content={label}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  </Tip>
);

type Props = {
  open: boolean;
  onClose: () => void;
  accountName: string;
  /** EB account id — used so a stale briefing from another account is not shown. */
  accountId?: string | null;
  briefing: XFactorKpiFullBriefing | null;
  loading: boolean;
  error?: string | null;
  categoryId: string;
  onCategoryIdChange: (categoryId: string) => void;
  onPrevAccount?: () => void;
  onNextAccount?: () => void;
  canPrevAccount?: boolean;
  canNextAccount?: boolean;
  /**
   * Label for the overall category in the modal title. Defaults to
   * "Overall (KPI Metrics)".
   */
  overallTitle?: string;
};

/**
 * The bubble click-through — single category/Overall pane with ←→ category
 * stepping. For Action → Full Briefing (all categories in one scroll), use
 * KpiFullBriefingModal instead.
 */
const KpiCategoryDetailModal: React.FC<Props> = ({
  open,
  onClose,
  accountName,
  accountId = null,
  briefing,
  loading,
  error,
  categoryId,
  onCategoryIdChange,
  onPrevAccount,
  onNextAccount,
  canPrevAccount = false,
  canNextAccount = false,
  overallTitle = "Overall (KPI Metrics)",
}) => {
  const ordered: XFactorKpiCategoryDetailModel[] = React.useMemo(() => {
    if (!briefing) return [];
    return [
      ...(briefing.overall ? [briefing.overall] : []),
      ...briefing.categories,
    ];
  }, [briefing]);
  const lookup = React.useMemo(() => buildMetricLookup(briefing), [briefing]);

  const currentIndex = ordered.findIndex(c => c.categoryId === categoryId);
  const rawCategory = currentIndex >= 0 ? ordered[currentIndex] : null;
  const canPrevCategory = currentIndex > 0;
  const canNextCategory =
    currentIndex >= 0 && currentIndex < ordered.length - 1;

  const briefingMatchesAccount =
    !accountId ||
    !briefing?.accountId ||
    String(briefing.accountId) === String(accountId);
  const showLoading =
    loading || (Boolean(accountId) && !briefingMatchesAccount);
  const category = showLoading || !briefingMatchesAccount ? null : rawCategory;

  const goPrevCategory = () => {
    if (canPrevCategory)
      onCategoryIdChange(ordered[currentIndex - 1].categoryId);
  };
  const goNextCategory = () => {
    if (canNextCategory)
      onCategoryIdChange(ordered[currentIndex + 1].categoryId);
  };

  const stepRef = React.useRef({
    left: goPrevCategory,
    right: goNextCategory,
    up: canPrevAccount ? onPrevAccount : undefined,
    down: canNextAccount ? onNextAccount : undefined,
  });
  stepRef.current = {
    left: goPrevCategory,
    right: goNextCategory,
    up: canPrevAccount ? onPrevAccount : undefined,
    down: canNextAccount ? onNextAccount : undefined,
  };
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      const step =
        e.key === "ArrowLeft"
          ? stepRef.current.left
          : e.key === "ArrowRight"
            ? stepRef.current.right
            : e.key === "ArrowUp"
              ? stepRef.current.up
              : e.key === "ArrowDown"
                ? stepRef.current.down
                : undefined;
      if (!step) return;
      e.preventDefault();
      step();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const jump = (key: KpiPaneAnchorKey) =>
    document
      .getElementById(kpiPaneAnchorId("kpi-detail", key))
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const isOverall = category?.categoryId === "overall";
  const title = category
    ? isOverall
      ? overallTitle
      : category.categoryName
    : null;
  const measurementDate = briefingMatchesAccount
    ? (briefing?.date ?? null)
    : null;
  const showBody = !showLoading && !error && category && category.hasData;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="max-w-4xl"
      title={
        <span className="flex items-center gap-4">
          {category ? <HeroScoreBubble score={category.score} /> : null}
          <span className="min-w-0 truncate text-xl font-bold leading-tight">
            {title ? `${title}: ` : null}
            <span style={{ color: ACCENT }}>{accountName}</span>
          </span>
        </span>
      }
      subtitle={
        briefing?.generatedAt
          ? `Briefing date: ${formatDMY(briefing.generatedAt)}`
          : measurementDate
            ? `Measurement date: ${formatDMY(measurementDate)}`
            : undefined
      }
      actions={
        loading && category ? (
          <Loader2
            className="h-5 w-5 animate-spin text-purple-500"
            aria-label="Loading"
          />
        ) : null
      }
    >
      <div className="flex gap-3">
        <nav className="sticky top-0 flex shrink-0 flex-col items-center gap-1 self-start">
          <RailButton
            Icon={ArrowUp}
            label="Previous account"
            onClick={onPrevAccount}
            disabled={!canPrevAccount}
          />
          <RailButton
            Icon={ArrowLeft}
            label="Previous category"
            onClick={goPrevCategory}
            disabled={!canPrevCategory}
          />
          <RailButton
            Icon={ArrowRight}
            label="Next category"
            onClick={goNextCategory}
            disabled={!canNextCategory}
          />
          <RailButton
            Icon={ArrowDown}
            label="Next account"
            onClick={onNextAccount}
            disabled={!canNextAccount}
          />
          {showBody && category ? (
            <div className="mt-6 flex flex-col items-center gap-1">
              {ANCHORS.filter(a => a.has(category)).map(a => (
                <RailButton
                  key={a.key}
                  Icon={a.Icon}
                  label={a.label}
                  onClick={() => jump(a.key)}
                />
              ))}
            </div>
          ) : null}
        </nav>

        <div className="min-w-0 flex-1">
          {showLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading briefing…
            </div>
          ) : error ? (
            <p className="m-0 text-sm text-amber-700">{error}</p>
          ) : !category ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This account has no KPI briefing yet for this measurement date.
            </div>
          ) : !category.hasData ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-medium">
                No {category.categoryName} data for this measurement date.
              </span>{" "}
              {category.emptyReason === "missing_inputs"
                ? "Required metrics haven't been scored yet."
                : category.emptyReason || ""}
            </div>
          ) : (
            <div className="grid gap-3">
              <KpiMetricsDataCard
                detail={category}
                peers={isOverall ? briefing?.categories : undefined}
                measurementDate={measurementDate}
              />
              <KpiCategoryPaneBody
                category={category}
                lookup={lookup}
                measurementDate={measurementDate}
                scope="kpi-detail"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default KpiCategoryDetailModal;
